import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { GiftCardPaymentModal } from './GiftCardPaymentModal';
import { GiftCardSuccessModal } from './GiftCardSuccessModal';
import { GiftCardGatewayModal } from './GiftCardGatewayModal';
import { useAuth } from '../context/AuthContext';
import { useGiftCardCart } from '../context/GiftCardCartContext';
import { fetchSettlementVerdict, fetchRefund } from '../lib/paymentStatus';
import { formatINR } from '../lib/money';

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
const API = `${BACKEND_URL}/api`;

const authHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/**
 * Paying for the whole gift-card basket.
 *
 * Two routes, tried in that order:
 *
 *   bulk        POST /api/giftcards/purchase/bulk — every line, one order, one
 *               payment. This is what a cart is supposed to mean, and it is
 *               specified in docs/GIFTCARD_BACKEND_HANDOVER.md §2.
 *   per-line    POST /api/giftcards/purchase, walked line by line. The endpoint
 *               that exists today takes exactly one reward and opens one payment
 *               session, so this settles the basket as N payments.
 *
 * The bulk call is attempted first and the fallback only runs on a 404/405 — the
 * day the endpoint ships, this becomes a single payment on its own with no code
 * change here.
 *
 * The fallback is honest rather than pretty: it says "Card 2 of 6" and it can
 * stop half way, leaving some vouchers bought and some not. That is survivable
 * only because a voucher is issued whole and instantly — a partial basket is an
 * incomplete purchase, never a corrupt one — and it is still better than making
 * the customer find each card again by hand.
 */
export function GiftCardCartCheckout() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { items, count, total, quote, removeGiftCard } = useGiftCardCart();

  const [chooserOpen, setChooserOpen] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [walletBalance, setWalletBalance] = useState(0);
  const [paymentModal, setPaymentModal] = useState({ open: false, url: '', orderId: '', status: 'pending' });
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [progress, setProgress] = useState(null);       // { done, total } during the per-line walk
  const [vouchers, setVouchers] = useState([]);
  const [showSuccess, setShowSuccess] = useState(false);

  const pollRef = useRef(null);
  // Resolved when the customer finishes (or abandons) the current payment, so
  // the per-line walk can wait for one card before starting the next.
  const settleRef = useRef(null);
  // Lines that have been paid for and are waiting to leave the basket.
  //
  // They cannot leave the moment payment lands, because this component owns the
  // success modal and CartPage only renders this component while the basket has
  // gift cards in it. Emptying the cart first swapped the page to "Your Cart is
  // Empty" and unmounted the modal that was about to say what had been bought —
  // the customer saw an empty cart and no voucher, with no way to tell a
  // successful purchase from a failed one.
  const purchasedRef = useRef([]);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  useEffect(() => stopPolling, [stopPolling]);

  const flushPurchased = useCallback(() => {
    purchasedRef.current.forEach((lineId) => removeGiftCard(lineId));
    purchasedRef.current = [];
  }, [removeGiftCard]);

  // Navigating away with the success modal still open must not leave paid-for
  // cards in the basket, where the next visit would buy them a second time.
  useEffect(() => flushPurchased, [flushPurchased]);

  useEffect(() => {
    if (!user) return;
    fetch(`${API}/wallet`, { headers: authHeaders(), credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d) setWalletBalance(Number(d.balance) || 0); })
      .catch(() => { /* no balance is a fine outcome — the chooser hides the option */ });
  }, [user]);

  /** Resolves with 'paid' | 'failed' once the gateway leg finishes. */
  const awaitGatewayLeg = ({ orderId, referenceId, url, amount }) => new Promise((resolve) => {
    settleRef.current = resolve;
    setPaymentModal({
      open: true,
      url,
      orderId,
      referenceId,
      amount,
      status: 'pending',
    });

    const check = async () => {
      const verdict = await fetchSettlementVerdict({ referenceId, orderId });

      if (verdict === 'paid') {
        stopPolling();
        setPaymentModal((p) => ({ ...p, status: 'success' }));
        setTimeout(() => {
          setPaymentModal({ open: false, url: '', orderId: '', status: 'pending' });
          settleRef.current?.('paid');
          settleRef.current = null;
        }, 1800);
      } else if (verdict === 'failed') {
        stopPolling();
        const refund = await fetchRefund(orderId);
        setPaymentModal((p) => ({ ...p, status: 'failed', refund }));
      }
      // 'pending' and 'unknown' keep polling — a dropped request says nothing
      // about the payment.
    };

    check();
    pollRef.current = setInterval(check, 3000);
  });

  const closeGateway = (verdict) => {
    stopPolling();
    setPaymentModal({ open: false, url: '', orderId: '', status: 'pending' });
    settleRef.current?.(verdict);
    settleRef.current = null;
  };

  const manualCheck = async () => {
    if (checkingStatus) return;
    setCheckingStatus(true);

    const verdict = await fetchSettlementVerdict({
      referenceId: paymentModal.referenceId,
      orderId: paymentModal.orderId,
    });

    if (verdict === 'paid') {
      stopPolling();
      setPaymentModal((p) => ({ ...p, status: 'success' }));
      setTimeout(() => closeGateway('paid'), 1800);
    } else if (verdict === 'failed') {
      stopPolling();
      const refund = await fetchRefund(paymentModal.orderId);
      setPaymentModal((p) => ({ ...p, status: 'failed', refund }));
    } else if (verdict === 'unknown') {
      toast.error('Could not reach the server. Your payment is safe — the check will retry on its own.');
    } else {
      toast.info('Not confirmed yet — this can take a few seconds. We are still checking.');
    }

    setCheckingStatus(false);
  };

  /** Voucher codes for an order, best-effort — the order page always has them. */
  const collectVouchers = async (orderId, voucherRef) => {
    try {
      let ref = voucherRef;
      if (!ref) {
        const res = await fetch(`${API}/orders/${orderId}`, { headers: authHeaders(), credentials: 'include' });
        if (!res.ok) return [];
        const data = await res.json();
        const inline = data?.vouchers || data?.order?.vouchers
          || (data.items || data.order?.order_items || [])[0]?.vouchers;
        if (inline?.length) return inline;
        ref = data.voucher_ref || data.order?.voucher_ref;
      }
      if (!ref) return [];
      const res = await fetch(`${API}/giftcards/razorpay/orders/${ref}`, { headers: authHeaders(), credentials: 'include' });
      if (!res.ok) return [];
      const data = await res.json();
      return data?.vouchers || data?.order?.vouchers || [];
    } catch {
      return [];
    }
  };

  /** One POST for the whole basket. Returns null when the endpoint is absent. */
  const tryBulk = async ({ paymentMethod, walletAmount }) => {
    const res = await fetch(`${API}/giftcards/purchase/bulk`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      credentials: 'include',
      body: JSON.stringify({
        items: items.map((line) => ({
          reward_id: line.reward_id,
          denomination: Math.round(line.denomination * 100),
          quantity: line.quantity,
        })),
        payment_method: paymentMethod,
        ...(walletAmount > 0 ? { wallet_amount: walletAmount } : {}),
      }),
    });

    // Not deployed yet — fall through to the per-line walk rather than failing.
    if (res.status === 404 || res.status === 405) return null;

    if (res.status === 409) {
      toast.info('This purchase is already being processed. Check My Orders in a moment.');
      return { handled: true };
    }
    if (!res.ok) {
      throw new Error((await res.json().catch(() => ({}))).detail || 'Payment could not be started.');
    }
    return res.json();
  };

  /** One line, through the endpoint that exists today. */
  const purchaseLine = async (line, paymentMethod, walletAmount) => {
    const res = await fetch(`${API}/giftcards/purchase`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      credentials: 'include',
      body: JSON.stringify({
        reward_id: line.reward_id,
        denomination: Math.round(line.denomination * 100),
        quantity: line.quantity,
        amount: line.denomination * line.quantity,
        payment_method: paymentMethod,
        ...(walletAmount > 0 ? { wallet_amount: walletAmount } : {}),
      }),
    });

    if (res.status === 409) return { duplicate: true };
    if (!res.ok) {
      throw new Error((await res.json().catch(() => ({}))).detail || `Could not buy ${line.name}.`);
    }
    return res.json();
  };

  /**
   * Proof, from the purchase response itself, that money actually moved.
   *
   * A missing `payment_url` means "no gateway leg" and nothing more — it is not
   * evidence of payment. GIFTCARD_BACKEND_HANDOVER.md §2 is explicit about it: a
   * wallet-settled order comes back with `wallet_debited: true`,
   * `payment_status: "paid"` and `status: "confirmed"`. Any one of them will do;
   * requiring all three would break on the first backend that sends two.
   */
  const settledInResponse = (payload) => {
    const body = payload?.data || payload || {};
    return body.wallet_debited === true
      || String(body.payment_status || '').toLowerCase() === 'paid'
      || ['confirmed', 'success', 'completed', 'paid'].includes(String(body.status || '').toLowerCase());
  };

  const settle = async (payload) => {
    // Both shapes, flat and wrapped in `data`, and both spellings of each key.
    // /purchase answers flat and snake_case; the wallet top-up endpoint has been
    // seen answering `data.payment_url`. Reading one spelling and treating the
    // miss as "no gateway leg" is exactly how a live payment goes unnoticed.
    const body = payload?.data || payload || {};
    const orderId = payload.order_id || payload.transaction_id
      || body.order_id || body.orderId || body.transaction_id || body.transactionId;
    const paymentUrl = payload.payment_url || body.payment_url || body.paymentUrl;
    const referenceId = payload.reference_id || body.reference_id || body.referenceId;
    const voucherRefs = [
      ...(payload.voucher_refs || body.voucher_refs || []),
      payload.voucher_ref || body.voucher_ref
    ].filter(Boolean);

    if (paymentUrl) {
      const verdict = await awaitGatewayLeg({
        orderId,
        referenceId,
        url: paymentUrl,
        amount: payload.total ?? payload.amount_due,
      });
      if (verdict !== 'paid') return { paid: false, vouchers: [] };
      
      let allVouchers = [];
      if (voucherRefs.length > 0) {
        for (const ref of voucherRefs) {
          const vs = await collectVouchers(orderId, ref);
          allVouchers = [...allVouchers, ...vs];
        }
      } else {
        allVouchers = await collectVouchers(orderId, null);
      }
      return { paid: true, vouchers: allVouchers };
    }

    // No gateway leg and no confirmation. This used to return `paid: true` — so
    // an order that never started emptied the basket and announced vouchers that
    // were never issued, which is the worst of the three things it could do.
    if (!settledInResponse(payload)) {
      // Logged, because this is a backend contract break and the response body
      // is the only thing that says which part of it broke.
      console.error('Gift card purchase returned neither a payment_url nor a settlement:', payload);
      throw new Error(
        payload.message || payload.detail
        // Not "nothing was charged" — we cannot see the money from here. What we
        // can promise is that no voucher was issued and the basket is intact.
        || 'Payment could not be started, so nothing was bought. Your cart is untouched — please try again.'
      );
    }

    let allVouchers = [];
    if (voucherRefs.length > 0) {
      for (const ref of voucherRefs) {
        const vs = await collectVouchers(orderId, ref);
        allVouchers = [...allVouchers, ...vs];
      }
    } else {
      allVouchers = await collectVouchers(orderId, null);
    }
    
    return { paid: true, vouchers: allVouchers };
  };

  const onConfirm = async ({ paymentMethod, walletAmount }) => {
    setProcessing(true);
    setChooserOpen(false);

    try {
      const bulk = await tryBulk({ paymentMethod, walletAmount });

      if (bulk?.handled) return;

      if (bulk) {
        const { paid, vouchers: got } = await settle(bulk);
        if (!paid) { toast.error('Payment was not completed. Your cart is untouched.'); return; }
        purchasedRef.current = items.map((line) => line.line_id);
        setVouchers(got);
        setShowSuccess(true);
        return;
      }

      // ── Per-line walk ────────────────────────────────────────────────────
      // The chooser priced one basket, so its wallet figure has to be spent
      // across several independent orders. It is walked down line by line: a
      // line the remaining balance covers in full goes to the wallet, and the
      // first line it cannot cover takes what is left and sends the remainder
      // to the gateway — the split the single-card endpoint already supports.
      // Passing the basket's wallet figure to every line would try to spend it
      // once per card.
      let walletLeft = Number(walletAmount) || 0;
      const bought = [];
      let collected = [];

      for (let i = 0; i < items.length; i += 1) {
        const line = items[i];
        setProgress({ done: i, total: items.length, name: line.name });

        const lineTotal = line.denomination * line.quantity;
        const lineWallet = Math.min(walletLeft, lineTotal);
        const lineMethod = lineWallet >= lineTotal ? 'wallet' : 'online';

        // Caught per line rather than around the walk: a card that fails halfway
        // must not take the cards already bought before it down with it, leaving
        // them paid for and still sitting in the basket.
        let stopped = false;
        try {
          const payload = await purchaseLine(line, lineMethod, lineWallet);
          // Only once the call succeeded — a rejected line debited nothing.
          walletLeft -= lineWallet;
          if (payload.duplicate) { bought.push(line); continue; }

          const { paid, vouchers: got } = await settle(payload);
          if (!paid) {
            stopped = true;
            toast.error(`Stopped at ${line.name}. ${bought.length} of ${items.length} cards were bought — the rest are still in your cart.`);
          } else {
            bought.push(line);
            collected = [...collected, ...got];
          }
        } catch (lineError) {
          stopped = true;
          toast.error(lineError.message);
        }
        if (stopped) break;
      }

      purchasedRef.current = bought.map((line) => line.line_id);

      if (bought.length) {
        setVouchers(collected);
        setShowSuccess(true);
      } else {
        flushPurchased();
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setProcessing(false);
      setProgress(null);
    }
  };

  const startCheckout = () => {
    if (!user) {
      toast.info('Please login to continue checkout');
      navigate('/login?redirect=/cart');
      return;
    }
    setChooserOpen(true);
  };

  // An empty basket has no summary to show — but the modals must outlive it. In
  // a mixed cart the last gift card can leave while this component stays mounted
  // beside the product summary, and the payment it is settling is still live.
  const settling = processing || paymentModal.open || showSuccess;
  if (!items.length && !settling) return null;

  /*
    Order totals, straight from /giftcards/quote — rupees, and already summed
    across the basket. It runs the same path as /purchase/bulk, so what is shown
    is what will be charged by construction rather than because two
    implementations agree today.

    The stored sum is the fallback for the moment before the first quote lands
    and for a quote that fails; it is never what checkout charges.
  */
  const payable = quote ? Number(quote.total) : total;
  const faceTotal = quote ? Number(quote.gift_card_value) : total;
  const savedTotal = quote ? Number(quote.discount) || 0 : 0;
  const surchargeTotal = quote ? Number(quote.surcharge) || Math.max(0, payable - faceTotal) : Math.max(0, payable - faceTotal);
  const cardCount = quote?.card_count ?? count;
  const unavailable = quote?.unavailable_count || 0;

  return (
    <>
      {items.length > 0 && (
      <div className="sticky top-24 rounded-3xl bg-surface p-6" data-testid="gift-card-summary">
        <h2 className="mb-6 font-heading text-xl text-secondary">Gift Card Summary</h2>

        <dl className="space-y-3 font-body text-sm">
          {/*
            Financial totals, not a list of brand names. A corporate basket can
            run to dozens of lines, and re-listing every one here answered a
            question the cart above already answered — while leaving the ones
            that matter at checkout unanswered.
          */}
          <div className="flex justify-between">
            <dt className="text-muted-foreground">Items</dt>
            <dd className="font-medium text-secondary">{cardCount}</dd>
          </div>

          <div className="flex justify-between">
            <dt className="text-muted-foreground">Total gift card value</dt>
            <dd className="font-medium text-secondary">{formatINR(faceTotal)}</dd>
          </div>

          {savedTotal > 0 && (
            <div className="flex justify-between text-success">
              <dt>Total discount</dt>
              <dd className="font-medium">−{formatINR(savedTotal)}</dd>
            </div>
          )}

          {surchargeTotal > 0 && (
            <div className="flex justify-between text-amber-600">
              <dt>Total surcharge</dt>
              <dd className="font-medium">+{formatINR(surchargeTotal)}</dd>
            </div>
          )}

          {/* Spelled out rather than omitted: a customer who has bought physical
              goods here expects a delivery line and reads its absence as a bug. */}
          <div className="flex justify-between">
            <dt className="text-muted-foreground">Delivery</dt>
            <dd className="font-medium text-success">FREE · Instant</dd>
          </div>

          <div className="flex items-baseline justify-between border-t border-gray-200 pt-3">
            <dt className="font-semibold text-secondary">Amount payable</dt>
            <dd className="font-body text-2xl font-bold text-secondary" data-testid="gift-card-total">
              {formatINR(payable)}
            </dd>
          </div>
        </dl>

        <Button
          onClick={startCheckout}
          disabled={processing || unavailable > 0}
          className="btn-press mt-6 w-full rounded-full bg-primary py-6 font-body text-white hover:bg-primary/90"
          data-testid="gift-card-checkout-btn"
        >
          {processing
            ? (progress ? `Card ${progress.done + 1} of ${progress.total}…` : 'Processing…')
            : `Proceed to checkout · ${formatINR(payable)}`}
        </Button>

        <p className="mt-3 text-center font-body text-[11px] text-muted-foreground">
          {unavailable > 0
            ? `${unavailable} card${unavailable === 1 ? '' : 's'} in your cart cannot be bought right now — remove ${unavailable === 1 ? 'it' : 'them'} to continue.`
            : 'Vouchers are issued the moment payment clears.'}
        </p>
      </div>
      )}

      <GiftCardPaymentModal
        open={chooserOpen}
        onClose={() => setChooserOpen(false)}
        onConfirm={onConfirm}
        productName={`${count} gift ${count === 1 ? 'card' : 'cards'}`}
        // The quoted total, not the local sum: the chooser has to price what the
        // summary beside it promises and what the gateway will charge.
        amount={payable}
        quantity={1}
        walletBalance={walletBalance}
        processing={processing}
        formatPrice={formatINR}
      />

      <GiftCardSuccessModal
        isOpen={showSuccess}
        // The bought lines leave the basket here, once they have been announced
        // — not when the payment landed. See `purchasedRef`.
        onClose={() => { setShowSuccess(false); flushPurchased(); navigate('/dashboard/orders'); }}
        vouchers={vouchers}
      />

      {/* Gateway leg. Same shape as the product page's, because it is the same
          provider screen — the difference is that this one can run more than
          once, and says which card it is on. */}
      <GiftCardGatewayModal
        open={paymentModal.open}
        url={paymentModal.url}
        orderId={paymentModal.orderId}
        amount={paymentModal.amount}
        status={paymentModal.status}
        checking={checkingStatus}
        refund={paymentModal.refund}
        progressLabel={progress ? `card ${progress.done + 1} of ${progress.total}` : undefined}
        onCheckStatus={manualCheck}
        onClose={() => {
          const wasPending = paymentModal.status === 'pending';
          closeGateway(wasPending ? 'cancelled' : 'failed');
          if (wasPending) toast('Payment window closed.', { icon: 'ℹ️' });
        }}
      />
    </>
  );
}

export default GiftCardCartCheckout;
